/**
 * Everything the front page says, kept apart from how it is drawn.
 *
 * Prices first, on purpose: they are the part most likely to change, and the
 * owner should be able to change them here without reading any markup. Every
 * figure is in Kenya shillings per month.
 */

/*
 * ── Prices ──────────────────────────────────────────────────────────────────
 *
 * PROPOSED, NOT CONFIRMED. These were chosen for a small-to-mid Kenyan ISP and
 * have not been agreed by the owner. Change the numbers here and the page, the
 * annual figures and the demo form all follow.
 *
 * The limits are a sales promise, not something the software enforces: nothing
 * in Dispatch counts technicians or jobs against a package.
 */
export const PRICING = {
  currency: 'KES',
  /** Paying for a year up front: this many months are charged for twelve. */
  annualMonthsCharged: 10,
  plans: [
    {
      id: 'starter',
      name: 'Starter',
      monthlyKes: 4_500,
      fromPrice: false,
      forWho: 'One office and a small field team getting off spreadsheets and WhatsApp groups.',
      limits: ['Up to 5 technicians', 'Up to 300 jobs a month', '3 office logins'],
      features: [
        'Dispatch board and ticket list',
        'Job links on the technician’s phone, working offline',
        'Customer tracking links and visit ratings',
        'SLA clocks with warnings and breaches',
        'SMS and email updates to customers and technicians',
        'Areas and zones',
      ],
    },
    {
      id: 'growth',
      name: 'Growth',
      monthlyKes: 12_000,
      fromPrice: false,
      featured: true,
      forWho: 'An ISP that bills, sells and keeps stock, with several teams in the field.',
      limits: ['Up to 20 technicians', 'Up to 1,500 jobs a month', '10 office logins'],
      features: [
        'Everything in Starter',
        'M-Pesa invoices, payments and arrears',
        'Contracts signed on the phone',
        'Leads, coverage checks and referrals',
        'Stock, van stock and purchasing',
        'Network map and design',
        'Dashboard and reports with CSV export',
      ],
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      monthlyKes: 30_000,
      fromPrice: true,
      forWho: 'Larger operators with many areas, a back office and their own ways of working.',
      limits: ['Technicians and jobs as agreed', 'Office logins as agreed'],
      features: [
        'Everything in Growth',
        'Building power and KPLC token purchases',
        'Attendance, leave, expenses and allowances',
        'Employment and landlord agreements',
        'Custom groups, roles and access requests',
        'Google and Microsoft sign-in',
        'Audit log of every change',
      ],
    },
  ],
} as const;

export type PlanId = (typeof PRICING.plans)[number]['id'];

/** "KES 12,000". Whole shillings; no decimals on a price list. */
export function kes(amount: number): string {
  return `${PRICING.currency} ${Math.round(amount).toLocaleString('en-KE')}`;
}

/** What a year costs paid up front, and what that saves on twelve monthly bills. */
export function annualPrice(monthlyKes: number): {
  total: number;
  saving: number;
} {
  const total = monthlyKes * PRICING.annualMonthsCharged;
  return { total, saving: monthlyKes * 12 - total };
}

/*
 * ── Contact ─────────────────────────────────────────────────────────────────
 */
export const COMPANY_SITE = 'https://gospeednet.co.ke';

/*
 * Customer care, for a customer who would rather talk to somebody than paste
 * a link. Shown as dialled locally; the links carry the international form so
 * they work from any phone.
 */
export const CUSTOMER_CARE = {
  display: '0798 840 098',
  tel: 'tel:+254798840098',
  whatsapp: 'https://wa.me/254798840098',
} as const;

/*
 * ── What it does ────────────────────────────────────────────────────────────
 *
 * Only what the code does today. Each group maps to a part of the console (or
 * the technician's and customer's pages); if a line here stops being true, take
 * it out.
 */
export type FeatureGroup = {
  id: string;
  code: string;
  title: string;
  lead: string;
  items: readonly string[];
};

export const FEATURE_GROUPS: readonly FeatureGroup[] = [
  {
    id: 'dispatch',
    code: 'D-01',
    title: 'Dispatch board and tickets',
    lead: 'Every installation, fault and visit on one board, with an owner, a priority and a clock.',
    items: [
      'Board by state — new, assigned, in progress, resolved — plus a searchable list of every ticket',
      'Website forms raise tickets straight onto the board, signed so nobody else can',
      'One fibre cut can carry twenty customer tickets and be resolved in one action',
      'Pick the area and see which technicians cover that zone before assigning',
      'Followers on CC get a read-only link to one ticket',
    ],
  },
  {
    id: 'field',
    code: 'F-02',
    title: 'The job on the technician’s phone',
    lead: 'No app to install and no password: a signed link to one job.',
    items: [
      'Address, customer, access details and the work, with one-tap acknowledge, arrive and complete',
      'Photos, checklists, materials used and a signature come back from site',
      'Works offline: actions and photos queue on the phone and sync without duplicating',
      'My work, performance and allowances for each technician',
    ],
  },
  {
    id: 'customer',
    code: 'C-03',
    title: 'Customers who know where their job is',
    lead: 'Fewer “where is your technician?” calls to the office.',
    items: [
      'A tracking link showing how far the job has gone',
      'Updates as the job moves: logged, on the way, resolved',
      'A rating request when the visit is done',
      'Customer records with their history in one place',
    ],
  },
  {
    id: 'sla',
    code: 'S-04',
    title: 'SLAs that are kept, not explained',
    lead: 'Every job type carries a time limit, measured in business hours.',
    items: [
      'Clocks pause while you wait on the customer, on parts or on access',
      'Warnings before a breach, and the breach recorded once, as it happened',
      'Visits that run past their time on site are flagged once, when they overrun',
    ],
  },
  {
    id: 'money',
    code: 'M-05',
    title: 'M-Pesa billing and power tokens',
    lead: 'Invoices and payments where your customers already pay.',
    items: [
      'Subscriptions, invoices and arrears; an issued invoice is never edited, only voided and replaced',
      'M-Pesa payments confirmed with Safaricom before they count',
      'Buy KPLC tokens for building meters and record their delivery',
    ],
  },
  {
    id: 'contracts',
    code: 'K-06',
    title: 'Contracts signed on the phone',
    lead: 'Sent as a link, signed on the spot, with a copy kept for everyone.',
    items: [
      'Customer agreements, staff letters of appointment and landlord building-access agreements',
      'Your own contract wording, with the company signing first',
      'Printable documents with a cover and watermark',
    ],
  },
  {
    id: 'selling',
    code: 'L-07',
    title: 'Selling',
    lead: 'From an enquiry to a connected customer without retyping anything.',
    items: [
      'Leads with activity, status and a coverage check',
      'Convert a lead into a customer and an installation job',
      'Referral codes, approval and payout tracking',
      'Sales report',
    ],
  },
  {
    id: 'network',
    code: 'N-08',
    title: 'Network, areas and stock',
    lead: 'Where your network runs and what it is built from.',
    items: [
      'Network map and a design editor for your routes and sites',
      'Areas grouped into zones, used for dispatch and reporting',
      'Stock, each technician’s van stock, stock counts and reorder levels',
      'Purchase orders for what you buy in',
    ],
  },
  {
    id: 'people',
    code: 'P-09',
    title: 'People and access',
    lead: 'The right screens for each person, and a record of who did what.',
    items: [
      'Groups and roles: admin, dispatcher, technician, sales, HR, accountant',
      'Google or Microsoft sign-in, with access requests the admin approves',
      'Attendance, leave, expenses and fuel claims',
      'Audit log of every change, with the name against it',
    ],
  },
  {
    id: 'numbers',
    code: 'R-10',
    title: 'Numbers and notifications',
    lead: 'What happened this week, and who was told.',
    items: [
      'Dashboard and reports, downloadable as CSV',
      'Notifications by SMS, email and WhatsApp, with your own message templates',
      'Failed sends kept and retried, so nothing disappears quietly',
      'In-app news of every new version',
    ],
  },
];

/*
 * ── How it works ────────────────────────────────────────────────────────────
 * A real sequence, so it is numbered.
 */
export const STEPS = [
  {
    title: 'A job is logged',
    body: 'From your website form, a call to the office, or a lead that has been sold. It lands on the board with a type, a priority and an SLA clock.',
  },
  {
    title: 'The office assigns it',
    body: 'The dispatcher picks the area, sees who covers that zone and assigns. The technician gets the job link by SMS, email or WhatsApp.',
  },
  {
    title: 'The technician does the work',
    body: 'Acknowledge, arrive and complete from the phone — with photos, materials and the customer’s signature, even with no signal.',
  },
  {
    title: 'The customer is kept informed',
    body: 'Updates go out as the job moves, the tracking link shows where it is, and a rating request follows when it is fixed.',
  },
  {
    title: 'You see the numbers',
    body: 'SLA performance, jobs by area and technician, sales and payments — on the dashboard and as CSV.',
  },
] as const;

/*
 * ── The demo board ──────────────────────────────────────────────────────────
 * Sample jobs only. Nothing here is real and nothing is sent anywhere.
 */
export const DEMO_STAGES = [
  {
    id: 'new',
    label: 'New',
    note: 'Logged from the website form. SLA clock started.',
  },
  {
    id: 'assigned',
    label: 'Assigned',
    note: 'Sent to the technician who covers this zone.',
  },
  {
    id: 'onsite',
    label: 'On site',
    note: 'Technician arrived. Photos and readings coming in.',
  },
  {
    id: 'resolved',
    label: 'Resolved',
    note: 'Fixed and signed for. Rating request sent.',
  },
] as const;

/** What the customer is sent at each stage. `{ref}` is the job's reference. */
export const DEMO_UPDATES = [
  'We have logged your fault, ref {ref}. Follow it here: dispatch…/track/…',
  'Your job {ref} is assigned. Your technician will call before coming.',
  'Your technician has arrived for {ref}.',
  'Job {ref} is resolved. How did we do? Rate the visit: dispatch…/track/…',
] as const;

export type DemoJob = {
  ref: string;
  title: string;
  area: string;
  stage: number;
  tech: string | null;
};

export const DEMO_JOBS: readonly DemoJob[] = [
  {
    ref: 'DEMO-104',
    title: 'No internet since morning',
    area: 'Zone A · Area 3',
    stage: 0,
    tech: null,
  },
  {
    ref: 'DEMO-101',
    title: 'New fibre installation',
    area: 'Zone B · Area 1',
    stage: 1,
    tech: 'Technician 2',
  },
  {
    ref: 'DEMO-102',
    title: 'Router keeps restarting',
    area: 'Zone A · Area 7',
    stage: 2,
    tech: 'Technician 1',
  },
  {
    ref: 'DEMO-099',
    title: 'Move router upstairs',
    area: 'Zone C · Area 2',
    stage: 3,
    tech: 'Technician 3',
  },
];

/** One step forward for one job; a resolved job stays resolved. */
export function advanceDemo(jobs: readonly DemoJob[], ref: string): DemoJob[] {
  const last = DEMO_STAGES.length - 1;
  return jobs.map((job) =>
    job.ref === ref && job.stage < last ? { ...job, stage: job.stage + 1, tech: job.tech ?? 'Technician 1' } : job,
  );
}

/** The job the demo moves next: the chosen one if it can move, else the first that can. */
export function nextDemoTarget(jobs: readonly DemoJob[], selected: string | null): string | null {
  const last = DEMO_STAGES.length - 1;
  const chosen = jobs.find((job) => job.ref === selected);
  if (chosen && chosen.stage < last) return chosen.ref;
  return jobs.find((job) => job.stage < last)?.ref ?? null;
}

/*
 * ── How you pay ─────────────────────────────────────────────────────────────
 * No account numbers here: they come on the invoice, so they cannot go stale
 * on a web page or be copied by somebody impersonating us.
 */
export const PAYMENT_METHODS = [
  {
    title: 'M-Pesa',
    body: 'Pay the invoice by M-Pesa. The paybill or till number is printed on every invoice we send.',
  },
  {
    title: 'Bank transfer',
    body: 'Our bank details come with the invoice. Use the invoice number as the reference.',
  },
  {
    title: 'Card',
    body: 'Visa or Mastercard, by a payment link sent with the invoice.',
  },
] as const;

/*
 * ── Questions ───────────────────────────────────────────────────────────────
 */
export const FAQS = [
  {
    q: 'Do technicians need to install an app?',
    a: 'No. Each job arrives as a link by SMS, email or WhatsApp and opens in the phone’s browser. It keeps working without signal and sends everything when the phone reconnects.',
  },
  {
    q: 'Do my customers need an account?',
    a: 'No. They get a tracking link for their own job, updates as it moves, and a rating request at the end.',
  },
  {
    q: 'Can my website send jobs and leads straight in?',
    a: 'Yes. Your website’s forms can post to Dispatch with a signed request, and each submission becomes a ticket on the board.',
  },
  {
    q: 'How do M-Pesa payments work?',
    a: 'Customers pay invoices by M-Pesa, and each payment is confirmed with Safaricom before it is counted against the invoice. You use your own M-Pesa credentials.',
  },
  {
    q: 'What happens to our data?',
    a: 'Each company’s records are kept separate, every change is in the audit log with a name against it, and you can download your reports as CSV at any time.',
  },
  {
    q: 'Can we change package later?',
    a: 'Yes. Move up or down at the start of any billing month. Paying annually gets you twelve months for the price of ten.',
  },
  {
    q: 'How do we get started?',
    a: 'Book a demo. We walk through Dispatch with your own job types and areas, then set up your account and staff logins.',
  },
] as const;

/*
 * ── Booking a demo ──────────────────────────────────────────────────────────
 *
 * There is no public endpoint a browser may post to: the website intake
 * (/webhooks/intake) only accepts requests signed with a server-side secret.
 * So the request is written out as a WhatsApp message to customer care, which
 * the visitor sends themselves — nothing is stored or sent by this page.
 */
export type DemoRequest = {
  name: string;
  company: string;
  phone: string;
  email: string;
  teamSize: string;
  plan: string;
};

export const TEAM_SIZES = ['1–5 technicians', '6–20 technicians', '21–50 technicians', 'More than 50'] as const;

/** The fields we cannot book a demo without, by name. Empty when it can be sent. */
export function missingDemoFields(request: DemoRequest): (keyof DemoRequest)[] {
  const missing: (keyof DemoRequest)[] = [];
  if (!request.name.trim()) missing.push('name');
  if (!request.company.trim()) missing.push('company');
  if (request.phone.replace(/\D/g, '').length < 9) missing.push('phone');
  return missing;
}

export function demoRequestMessage(request: DemoRequest): string {
  const plan = PRICING.plans.find((p) => p.id === request.plan);
  const lines = [
    'Hello GoSpeedNet, I would like a demo of Dispatch.',
    `Name: ${request.name.trim()}`,
    `Company: ${request.company.trim()}`,
    `Phone: ${request.phone.trim()}`,
  ];
  if (request.email.trim()) lines.push(`Email: ${request.email.trim()}`);
  if (request.teamSize) lines.push(`Team: ${request.teamSize}`);
  lines.push(`Package: ${plan ? plan.name : 'Not sure yet'}`);
  return lines.join('\n');
}

export function demoWhatsAppLink(request: DemoRequest): string {
  return `${CUSTOMER_CARE.whatsapp}?text=${encodeURIComponent(demoRequestMessage(request))}`;
}
